import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowLeft, Flag, Package, PackageMinus, PackageOpen, CheckCircle, Search, RefreshCw, Timer, ScanLine, PackageSearch, Route } from "lucide-react";
import { useTranslation } from "../i18n";
import { toast } from "react-toastify";
import { playSuccess, playError } from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import FlowLoader from "../components/FlowLoader";
import FlaggedResolveSplitButton from "../components/dispatch/flagged-resolve-split-button";
import FlaggedRefundModal from "../components/dispatch/flagged-refund-modal";
import type { RefundLine } from "../components/dispatch/flagged-refund-modal";
import FlaggedManualShipModal from "../components/dispatch/flagged-manual-ship-modal";
import type { ShipLine } from "../components/dispatch/flagged-manual-ship-modal";
import ReplacementOrderModal from "../components/dispatch/replacement-order-modal";
import { postResolution, resolveAttribution } from "../components/dispatch/resolve-api";
import type { DispatchOrder, DispatchOrderItem, FlaggedPickTrace, FlaggedShortfallStage } from "../types/Dispatch";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";
import FlaggedPicksPanel from "../components/dispatch/flagged-picks-panel";
import { displayItemCode } from "../components/dispatch/item-code";
import { isVisible, useHomeModuleVisibility, readUserRole } from "../components/helpers/homeModuleVisibility";
import "../styles/dispatch.css";
import "../styles/flagged-picks.css";

const toDateStr = (d: Date) => d.toISOString().slice(0, 10);
const todayStr = () => toDateStr(new Date());

type DatePreset = "all" | "today" | "yesterday" | "last_7" | "last_30" | "last_60" | "custom";
// "all" leads: the flagged list is an open-problem worklist, so the default
// view must surface every unresolved flag regardless of when it was raised —
// a date-scoped default silently hides orders flagged on an earlier day.
const DATE_PRESETS: DatePreset[] = ["all", "today", "yesterday", "last_7", "last_30"];

const daysAgo = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return toDateStr(d);
};

const presetRange = (p: DatePreset): { from: string; to: string } => {
    const today = todayStr();
    switch (p) {
        case "all": return { from: "", to: "" };
        case "today": return { from: today, to: today };
        case "yesterday": { const y = daysAgo(1); return { from: y, to: y }; }
        case "last_7": return { from: daysAgo(6), to: today };
        case "last_30": return { from: daysAgo(29), to: today };
        case "last_60": return { from: daysAgo(59), to: today };
        default: return { from: "", to: "" };
    }
};

const getUserWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        if (raw) return JSON.parse(raw).warehouse || "Newport";
    } catch { /* fallthrough */ }
    return "Newport";
};

// "How long has this order been flagged?" — relative to NOW.
//   • <24h elapsed → "{m}m {h}h" (minutes + hours)
//   • ≥24h elapsed → "{h}h {d}d" (hours + days)
// Returns "—" if the timestamp is missing.
const formatFlagDuration = (flaggedAt: string | undefined | null, now: number): string => {
    if (!flaggedAt) return "—";
    const t = Date.parse(flaggedAt);
    if (!Number.isFinite(t)) return "—";
    const totalMin = Math.max(0, Math.floor((now - t) / 60_000));
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;
    if (totalMin >= 1440) return `${hours}h ${days}d`;
    return `${mins}m ${hours}h`;
};

// Format an average duration in seconds into the same {m}m {h}h / {h}h {d}d
// form used by the per-card timer. Used for the KPI strip.
const formatAverageSeconds = (seconds: number | null | undefined): string => {
    if (seconds == null) return "—";
    const totalMin = Math.max(0, Math.floor(seconds / 60));
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;
    if (totalMin >= 1440) return `${hours}h ${days}d`;
    return `${mins}m ${hours}h`;
};

// Triage urgency, derived purely from how long the flag has been open.
// Every flagged order is a customer waiting on an answer, so age IS the
// priority — the card's accent stripe and the age pill both read from
// this so a leader can see the queue's shape without reading a single
// timestamp.
//   • fresh  (<4h)   — raised this shift, still inside a normal response.
//   • warn   (4–24h) — slipping; it will miss today's dispatch cut-off.
//   • urgent (≥24h)  — already missed a cut-off, oldest work first.
type FlagAge = "fresh" | "warn" | "urgent";

export const flagAgeTier = (flaggedAt: string | undefined | null, now: number): FlagAge => {
    if (!flaggedAt) return "fresh";
    const t = Date.parse(flaggedAt);
    if (!Number.isFinite(t)) return "fresh";
    const hours = (now - t) / 3_600_000;
    if (hours >= 24) return "urgent";
    if (hours >= 4) return "warn";
    return "fresh";
};

const formatMoney = (value: number | undefined, currency: string | undefined) => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "GBP" }).format(value);
    } catch {
        return `${currency || "GBP"} ${value.toFixed(2)}`;
    }
};

// Free-text match for one flagged order — the search box and the
// scan-to-filter path below share it so they can never disagree about
// what "found" means.
//
// Barcodes are matched across EVERY scannable code on the line, not just
// item.barcode. For most of the catalogue products.barcode holds the
// ASIN, and the real EAN — the number actually printed on the box the
// leader is holding — exists only in sku_barcodes. The BE already ships
// those as item.barcodes (barcodesOf(product_barcode, alt_barcodes));
// matching the single legacy field meant typing or scanning a real
// barcode here silently found nothing for the bulk of stock.
//
// external_sku is included too: that is the code the sales channel sent
// us, and it is what support quotes off a customer email when the line
// has been mapped to a different internal SKU.
//
// Case is folded because this is a FILTER, not a barcode lookup — the
// worst outcome is one extra card on screen. Scan resolution elsewhere
// in the app stays case-exact on purpose.
export const orderMatchesQuery = (order: DispatchOrder, query: string): boolean => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    const hit = (v: string | null | undefined) => !!v && v.toLowerCase().includes(needle);
    return hit(order.order_number)
        || hit(order.customer?.name)
        || hit(order.latest_flag?.flagged_by_name)
        || hit(order.latest_flag?.flagged_id)
        || order.items.some(i =>
            hit(i.sku)
            || hit(i.external_sku)
            || hit(i.barcode)
            || (i.barcodes ?? []).some(hit));
};

// Which of the two flag families an order belongs to. packed_at is the
// discriminator the resolve flow already relies on:
//   packing — flagged BEFORE it shipped (Packing/Label Issue at the
//             desk). No parcels exist, so Manually fulfil can't apply.
//   missing — shipped SHORT (auto-flag at fulfil time). Parcels exist;
//             the outstanding units are refunded or manually fulfilled.
export type FlagIssueKind = "packing" | "missing";
export const flagIssueKind = (o: DispatchOrder): FlagIssueKind =>
    o.packed_at ? "missing" : "packing";

// The server stamps this constant on auto-raised flags; the issue chip
// already says the same thing, so repeating it as the reason is noise.
const AUTO_FLAG_REASON = "Order contains flagged items";

// A pick line carries a shortfall signal when the picker ran a missing
// count there, or the line came up short / was flagged / never picked.
const isShortLine = (tr: FlaggedPickTrace): boolean =>
    tr.missing_check !== null
    || tr.qty_picked < tr.qty_required
    || tr.line_status === "missing"
    || tr.line_status === "flagged";

// One trace row per shelf, not per wave line: Pick & Sort can split a
// 10-unit order line into ten 1-unit lines across two shelves, and the
// leader checks shelves, not lines. Counts are summed; the latest pick
// supplies picker / time / tote / wave; the latest missing count wins.
export const groupTraceByShelf = (rows: FlaggedPickTrace[]): FlaggedPickTrace[] => {
    const out = new Map<string, FlaggedPickTrace>();
    for (const tr of rows) {
        const key = `${tr.sku}|${tr.location_label}`;
        const cur = out.get(key);
        if (!cur) {
            out.set(key, { ...tr });
            continue;
        }
        const later = (tr.picked_at ?? "") > (cur.picked_at ?? "");
        const laterCheck = tr.missing_check !== null
            && (cur.missing_check === null || tr.missing_check.at > cur.missing_check.at);
        out.set(key, {
            ...cur,
            qty_required: cur.qty_required + tr.qty_required,
            qty_picked: cur.qty_picked + tr.qty_picked,
            order_qty: cur.order_qty + tr.order_qty,
            picked_units: cur.picked_units + tr.picked_units,
            per_order: cur.per_order && tr.per_order,
            picked_at: later ? tr.picked_at : cur.picked_at,
            picker_name: later ? tr.picker_name : cur.picker_name,
            tote_id: later ? tr.tote_id : cur.tote_id,
            wave_id: later ? tr.wave_id : cur.wave_id,
            line_status: isShortLine(tr) && !isShortLine(cur) ? tr.line_status : cur.line_status,
            missing_check: laterCheck ? tr.missing_check : cur.missing_check,
        });
    }
    return [...out.values()];
};

// When every pick line for a short sku was scanned clean at the shelf,
// the unit went astray between the tote and the desk and no single shelf
// can be named. One compact row per sku then lists the shelves it came
// from, rather than a row per line.
interface CleanPickSummary {
    sku: string;
    shelves: string[];
    units: number;
    wave_id: number;
}

export const summariseCleanPicks = (rows: FlaggedPickTrace[]): CleanPickSummary[] => {
    const out = new Map<string, CleanPickSummary>();
    for (const tr of rows) {
        const cur = out.get(tr.sku) ?? { sku: tr.sku, shelves: [], units: 0, wave_id: tr.wave_id };
        if (!cur.shelves.includes(tr.location_label)) cur.shelves.push(tr.location_label);
        cur.units += tr.picked_units;
        cur.wave_id = Math.max(cur.wave_id, tr.wave_id);
        out.set(tr.sku, cur);
    }
    return [...out.values()];
};

// One plain sentence per missing sku: the stage it was lost at in bold,
// then who, where and when — so a team leader reads the card and knows
// what went wrong without decoding wave lines (Alex, 2026-10-13: "know
// exactly where and how the order went wrong as simply as possible").
export interface FlagStoryChip { label: string; qty: number | null; high: boolean }
export interface FlagStorySegment { heading: string; text: string; chips: FlagStoryChip[]; more: number }
export interface FlagStory { sku: string; segments: FlagStorySegment[] }

type Translate = (key: Parameters<ReturnType<typeof useTranslation>["t"]>[0], params?: Record<string, string>) => string;

export const buildFlagStories = (
    stages: FlaggedShortfallStage[],
    rows: FlaggedPickTrace[],
    t: Translate,
    fmt: (iso: string) => string,
): FlagStory[] => stages
    .filter(s => s.missing > 0)
    .map(s => {
        const skuRows = rows.filter(r => r.sku === s.sku);
        const short = groupTraceByShelf(skuRows.filter(isShortLine));
        const clean = summariseCleanPicks(skuRows.filter(r => !isShortLine(r)))[0] ?? null;
        const shared = skuRows.some(r => !r.per_order);
        const latest = skuRows
            .filter(r => r.picked_at)
            .sort((a, b) => ((a.picked_at ?? "") < (b.picked_at ?? "") ? 1 : -1))[0] ?? null;
        const segments: FlagStorySegment[] = [];

        if (s.picking > 0) {
            const facts = short.map(r => {
                const shelf = r.location_label;
                if (r.missing_check) {
                    const found = r.missing_check.found ?? 0;
                    const p = { by: r.missing_check.by || "—", shelf, when: fmt(r.missing_check.at), found: String(found) };
                    return found > 0 ? t("flag_story_checked_found", p) : t("flag_story_checked_none", p);
                }
                if (!r.picked_at && r.qty_picked === 0) return t("flag_story_never_picked", { shelf });
                return t("flag_story_short_pick", {
                    picked: String(r.qty_picked), required: String(r.qty_required), shelf,
                    by: r.picker_name || "—", when: r.picked_at ? fmt(r.picked_at) : "—",
                });
            });
            const chips: FlagStoryChip[] = [];
            let more = 0;
            if (s.unwaved > 0) {
                const stock = s.stock ?? [];
                facts.push(t(stock.length > 0 ? "flag_story_never_waved_stock" : "flag_story_never_waved_no_stock", { n: String(s.unwaved) }));
                for (const st of stock) chips.push({ label: st.location_label, qty: st.qty, high: st.high_low === "high" });
                more = Math.max(0, (s.stock_shelves ?? 0) - stock.length);
            }
            if (facts.length === 0) facts.push(t("flag_story_premarked"));
            segments.push({ heading: t("flag_story_missing_picking", { n: String(s.picking) }), text: facts.join(". "), chips, more });
        }

        if ((s.sorting ?? 0) > 0) {
            segments.push({ heading: t("flag_story_missing_sorting", { n: String(s.sorting) }), text: t("flag_story_not_sorted"), chips: [], more: 0 });
        }

        if (s.dispatch > 0) {
            const chips: FlagStoryChip[] = (clean?.shelves ?? []).map(label => ({ label, qty: null, high: false }));
            let text: string;
            if (clean && !shared) {
                text = t("flag_story_scanned_lost", { n: String(clean.units), by: latest?.picker_name || "—" });
                if (latest?.tote_id) text += ` (${t("flag_story_tote", { tote: latest.tote_id })})`;
            } else {
                text = t(s.sorting !== null ? "flag_story_sorted_lost" : "flag_story_picked_lost");
            }
            segments.push({ heading: t("flag_story_missing_dispatch", { n: String(s.dispatch) }), text, chips, more: 0 });
        }

        return { sku: s.sku, segments };
    })
    .filter(st => st.segments.length > 0);

type FlaggedSort = "oldest" | "newest";

interface FlaggedStats {
    open_count: number;
    resolved_count: number;
    avg_resolution_seconds: number | null;
}

const DispatchFlagged = () => {
    usePageTitle("flagged_orders_title");
    const { t } = useTranslation();
    const navigate = useNavigate();
    const readOnly = useReadOnly();
    const [orders, setOrders] = useState<DispatchOrder[]>([]);
    const [stats, setStats] = useState<FlaggedStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [scan, setScan] = useState("");
    const [datePreset, setDatePreset] = useState<DatePreset>("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouse] = useState(getUserWarehouse);
    // Without cross_warehouse the list is pinned to the person's own warehouse (no All, no country).
    const warehouse = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [issueFilter, setIssueFilter] = useState<"all" | FlagIssueKind>("all");
    const [sortBy, setSortBy] = useState<FlaggedSort>("oldest");
    // Bumped by the Refresh button to re-run the fetch without changing a
    // filter. Deliberately manual, not a poll: the list is read at a desk
    // and rows disappearing mid-click is worse than a stale count.
    const [refreshTick, setRefreshTick] = useState(0);
    // Quick-resolve modal (Refund / Manually ship / Send replacement)
    // opened from a card's split Resolve button. The replacement entry
    // carries the shortfall per line, computed ONCE when the menu item is
    // picked, so the modal's draft effect sees a stable object.
    const [quickAction, setQuickAction] = useState<
        | { order: DispatchOrder; mode: "refund" | "ship" }
        | { order: DispatchOrder; mode: "replace"; outstanding: { line_position: number; count: number }[] }
        | null
    >(null);
    const [quickBusy, setQuickBusy] = useState(false);
    // Where each flagged order's units were picked from, keyed by
    // order_id — fetched in one batch after the list lands, so the cards
    // render at once and the shelf detail fills in a beat later. A failed
    // or absent trace just leaves the chips as they were.
    const [pickTraces, setPickTraces] = useState<Record<string, FlaggedPickTrace[]>>({});
    const [shortfallStages, setShortfallStages] = useState<Record<string, FlaggedShortfallStage[]>>({});
    // Re-renders the per-card timers every minute so "23m 1h" ticks up.
    const [now, setNow] = useState(() => Date.now());
    // Flagged picks tile (2026-10-08): units a leader is fetching from high
    // racking for orders that would otherwise ship short. The tile shows
    // the open count and opens the panel in place of the orders list.
    const [picksView, setPicksView] = useState(false);
    const [picksTile, setPicksTile] = useState<{ open: number; available: boolean } | null>(null);
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);

    // Live warehouse registry grouped by country — same source and
    // grouping as the Search Orders toolbar, so this filter offers the
    // real (un-hidden) warehouses instead of a hard-coded trio. Falls
    // back to an empty list if /settings is unreachable — the dropdown
    // then collapses to "All warehouses" plus the user's own.
    const [warehousesByCountry, setWarehousesByCountry] = useState<{ country: string; warehouses: string[] }[]>([]);
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const rows: { warehouse: string; country: string | null }[] = await res.json();
                const map = new Map<string, string[]>();
                for (const row of rows) {
                    const country = row.country || "—";
                    if (!map.has(country)) map.set(country, []);
                    map.get(country)!.push(row.warehouse);
                }
                const groups = Array.from(map.entries())
                    .map(([country, ws]) => ({ country, warehouses: [...ws].sort() }))
                    .sort((a, b) => a.country.localeCompare(b.country));
                setWarehousesByCountry(groups);
            } catch { /* leave empty */ }
        })();
    }, []);

    // Collapse a "country:X" selection into that country's warehouses —
    // the /orders and /orders/flagged/stats endpoints both accept the
    // comma-separated form.
    const warehouseParam = useMemo(() => {
        if (!warehouse) return "";
        if (warehouse.startsWith("country:")) {
            const country = warehouse.slice("country:".length);
            const grp = warehousesByCountry.find(g => g.country === country);
            return grp ? grp.warehouses.join(",") : "";
        }
        return warehouse;
    }, [warehouse, warehousesByCountry]);

    // The single warehouse the Flagged picks tile reads (none for All / a
    // country). Found items (Poland, 2026-10-20): the tile stays on the row
    // like Pembroke's wherever the Found items module is visible, even with
    // nothing on the shelf yet.
    const tileWarehouse = (() => {
        const wh = warehouseParam || pickedWarehouse || "";
        return wh.startsWith("country:") || wh.includes(",") ? "" : wh;
    })();
    const tileModules = useHomeModuleVisibility(tileWarehouse);
    const foundModuleOn = !!tileWarehouse && isVisible(tileModules, "dispatch.found_items", readUserRole());

    const applyPreset = (p: DatePreset) => {
        setDatePreset(p);
        if (p !== "custom") {
            const { from, to } = presetRange(p);
            setDateFrom(from);
            setDateTo(to);
        }
    };

    // Fetch flagged orders + KPI stats in parallel. Date + warehouse +
    // status are filtered server-side; the stats endpoint covers the
    // whole flag history (open + resolved) scoped by warehouse only.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const params = new URLSearchParams();
                const wh = warehouseParam || pickedWarehouse;
                if (wh && !wh.startsWith("country:")) params.set("warehouse", wh);
                const res = await apiFetch(`/flagged-picks/list?${params}`, {});
                if (!alive || !res.ok) return;
                const data = await res.json();
                const tasks: { status: string }[] = Array.isArray(data.tasks) ? data.tasks : [];
                const available = data.available !== false;
                // Outside a Flagged Picks warehouse (Poland) the tile is the
                // found-items shelf: it counts what is on the shelf now.
                setPicksTile({
                    open: tasks.filter(x => available
                        ? x.status === "pending" || x.status === "picking" || x.status === "staged"
                        : x.status === "staged").length,
                    available,
                });
            } catch { /* the tile simply stays hidden */ }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseParam, pickedWarehouse, refreshTick]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const listParams = new URLSearchParams({ status: "flagged" });
                if (warehouseParam) listParams.set("warehouse", warehouseParam);
                if (dateFrom)  listParams.set("from", dateFrom);
                if (dateTo)    listParams.set("to", dateTo);
                const statsParams = new URLSearchParams();
                if (warehouseParam) statsParams.set("warehouse", warehouseParam);
                const [listRes, statsRes] = await Promise.all([
                    apiFetch(`/orders?${listParams}`, {}),
                    apiFetch(`/orders/flagged/stats?${statsParams}`, {}),
                ]);
                if (!alive) return;
                if (listRes.ok) {
                    const data = await listRes.json();
                    setOrders(Array.isArray(data.orders) ? data.orders : []);
                }
                if (statsRes.ok) {
                    const data = await statsRes.json();
                    setStats({
                        open_count: data.open_count ?? 0,
                        resolved_count: data.resolved_count ?? 0,
                        avg_resolution_seconds: data.avg_resolution_seconds ?? null,
                    });
                }
            } catch {
                if (alive) toast.error(t("server_error"), { toastId: "flagged-load-err" });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseParam, dateFrom, dateTo, refreshTick]);

    // Pick trace for the loaded orders (leaders' request, 2026-10-08:
    // "show us where the missing item was allegedly picked from so we can
    // check without going into logs"). One batched call per list load.
    useEffect(() => {
        const ids = orders.map(o => o.order_id);
        if (ids.length === 0) { setPickTraces({}); setShortfallStages({}); return; }
        let alive = true;
        (async () => {
            try {
                const params = new URLSearchParams({ order_ids: ids.join(",") });
                const res = await apiFetch(`/orders/flagged/pick-trace?${params}`, {});
                if (!alive || !res.ok) return;
                const data = await res.json();
                const traces = data?.traces;
                if (traces && typeof traces === "object" && !Array.isArray(traces)) {
                    setPickTraces(traces as Record<string, FlaggedPickTrace[]>);
                }
                const stages = data?.stages;
                if (stages && typeof stages === "object" && !Array.isArray(stages)) {
                    setShortfallStages(stages as Record<string, FlaggedShortfallStage[]>);
                }
            } catch { /* chips render without the shelf detail */ }
        })();
        return () => { alive = false; };
    }, [orders]);

    // Scan a FLG-YYMMDD-XXXX barcode (or order number) to jump straight
    // to the resolve page — same effect as clicking the row's Resolve
    // button. Three-tier lookup:
    //   1. order_number match in the loaded list — instant.
    //   2. latest_flag.flagged_id match in the loaded list — instant
    //      (the list response now includes the flag, so no extra fetch).
    //   3. FLG-shaped barcode that wasn't in the list (e.g. resolved or
    //      filtered out): hit /orders/flagged/by-id/:flagged_id to find
    //      the order across the entire flag history.
    const handleScan = useCallback(async (scannedValue?: string) => {
        const val = (scannedValue ?? scan).trim();
        if (!val) return;
        setScan("");

        // 1. Order number — fast in-memory match.
        const byOrderNum = orders.find(o => o.order_number === val);
        if (byOrderNum) {
            playSuccess();
            navigate(`/dispatch/flagged/${byOrderNum.order_id}`);
            return;
        }

        // 2. Currently-loaded latest flag — also fast in-memory.
        const upper = val.toUpperCase();
        const byFlagId = orders.find(o => o.latest_flag?.flagged_id?.toUpperCase() === upper);
        if (byFlagId) {
            playSuccess();
            navigate(`/dispatch/flagged/${byFlagId.order_id}`);
            return;
        }

        // 3. FLG-shaped barcode not in the visible list — single BE lookup.
        if (/^FLG-\d{6}-\d+$/i.test(val)) {
            try {
                const res = await apiFetch(
                    `/orders/flagged/by-id/${encodeURIComponent(val)}`,
                    {},
                );
                if (res.ok) {
                    const data = await res.json();
                    if (data?.order_id) {
                        playSuccess();
                        navigate(`/dispatch/flagged/${data.order_id}`);
                        return;
                    }
                }
            } catch { /* fall through to error toast */ }
        }

        // 4. Not an order and not a flag — try it as a PRODUCT. Scanning
        // the item in your hand should answer "which flagged orders are
        // waiting on this?". That is a filter, not a jump: several flagged
        // orders can share a SKU, and picking one of them for the leader
        // would be a guess. Feeding the scan into the search box also
        // leaves the term visible and clearable.
        if (orders.some(o => orderMatchesQuery(o, val))) {
            playSuccess();
            setQuery(val);
            return;
        }

        playError();
        toast.dismiss();
        toast.error(t("no_order_found"));
    }, [scan, orders, navigate, t]);

    // Apply text search + sort on top of the BE-filtered list. Default sort
    // is "oldest flagged first" so orders left longest unresolved float to
    // the top — same priority a leader would naturally pick from.
    const filtered = useMemo(() => {
        const q = query.trim();
        const inFamily = issueFilter === "all"
            ? orders.slice()
            : orders.filter(order => flagIssueKind(order) === issueFilter);
        const matched = q === ""
            ? inFamily
            : inFamily.filter(order => orderMatchesQuery(order, q));
        const flaggedTime = (o: DispatchOrder) =>
            Date.parse(o.latest_flag?.flagged_at ?? o.created_at) || 0;
        matched.sort((a, b) =>
            sortBy === "oldest"
                ? flaggedTime(a) - flaggedTime(b)
                : flaggedTime(b) - flaggedTime(a),
        );
        return matched;
    }, [orders, query, sortBy, issueFilter]);

    // How many of the loaded flags are already past a dispatch cut-off.
    // Shown on the Open KPI so the headline number carries its own
    // severity instead of needing the list read to find out.
    const urgentCount = useMemo(
        () => orders.filter(o => flagAgeTier(o.latest_flag?.flagged_at, now) === "urgent").length,
        [orders, now],
    );

    const formatDate = (iso: string) => {
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
        } catch { return iso; }
    };

    // Shared outcome handling for both quick actions. On success the card
    // leaves the list immediately AND the fetch re-runs so the KPI strip
    // agrees with it; on conflict (resolved elsewhere / stale card) the
    // list refreshes rather than retrying a write that can never land.
    const outstandingOf = (i: DispatchOrderItem) =>
        (i.missingCount || 0) + (i.damagedCount || 0) + (i.wrongItemCount || 0);

    const runQuickResolve = async (
        order: DispatchOrder,
        body: { refund_lines: RefundLine[]; ship_lines?: ShipLine[]; ship_tracking_number?: string },
        successToast: string,
    ) => {
        // Defense in depth — the split button already disables Refund/Manually
        // fulfil in read-only mode, so this should be unreachable, but the
        // write must never fire even if something upstream slips through.
        if (readOnly) return;
        setQuickBusy(true);
        const outcome = await postResolution(order.order_id, { ...resolveAttribution(), ...body });
        setQuickBusy(false);
        toast.dismiss();
        if (outcome === "ok") {
            playSuccess();
            toast.success(successToast);
            setQuickAction(null);
            setOrders(prev => prev.filter(o => o.order_id !== order.order_id));
            setRefreshTick(n => n + 1);
            return;
        }
        playError();
        if (outcome === "conflict") {
            toast.error(t("resolve_conflict"));
            setQuickAction(null);
            setRefreshTick(n => n + 1);
        } else if (outcome === "forbidden") {
            toast.error(t("resolve_not_permitted"));
            setQuickAction(null);
        } else {
            // Network/server failure: nothing was saved — keep the modal
            // open so the leader's edited counts aren't thrown away.
            toast.error(t("resolve_failed"));
        }
    };

    const handleQuickRefund = (order: DispatchOrder) => (refundLines: RefundLine[], totalRefund: number) => {
        const amount = formatMoney(totalRefund, order.currency);
        void runQuickResolve(
            order,
            { refund_lines: refundLines },
            totalRefund > 0 && amount
                ? t("refund_issued_for", { amount })
                : t("resolve_complete"),
        );
    };

    // Send replacement picked from the menu: snapshot the shortfall per
    // line now (the card could refresh underneath an open modal) and hand
    // it to the replacement draft as its default selection.
    const openReplace = (order: DispatchOrder) => {
        if (readOnly) return;
        const outstanding = order.items
            .filter(i => i.line_position !== undefined && outstandingOf(i) > 0)
            .map(i => ({ line_position: i.line_position as number, count: outstandingOf(i) }));
        setQuickAction({ order, mode: "replace", outstanding });
    };

    // The BE created the REP order AND closed the flag in one transaction,
    // so the card leaves the list exactly as it does after a quick refund.
    const handleReplacementCreated = (order: DispatchOrder) => (replacementNumber: string) => {
        playSuccess();
        toast.dismiss();
        toast.success(t("replacement_created_flag_resolved", { order: replacementNumber }));
        setQuickAction(null);
        setOrders(prev => prev.filter(o => o.order_id !== order.order_id));
        setRefreshTick(n => n + 1);
    };

    // The modal hands back what the leader stepped: units found go into the
    // chosen parcel, units not found ride along as refund lines and land in
    // Pending Refund — one resolve, so the flag never closes half-done.
    const handleQuickShip = (order: DispatchOrder) => (
        trackingNumber: string,
        shipLines: ShipLine[],
        refundLines: RefundLine[],
        refundTotal: number,
    ) => {
        const amount = formatMoney(refundTotal, order.currency);
        void runQuickResolve(
            order,
            { refund_lines: refundLines, ship_lines: shipLines, ship_tracking_number: trackingNumber },
            refundLines.length > 0
                ? t("manual_ship_done_partial", { amount: amount ?? "" })
                : t("manual_ship_done"),
        );
    };

    return (
        <div className="dispatch-page dispatch-flagged-page page-data" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
            <div className="page-title-bar">
                <h1 className="page-title">{t("flagged_orders_title")}</h1>
                <button className="page-back-btn" onClick={() => navigate("/dispatch/menu")}><ArrowLeft size={20} /></button>
            </div>

            {/* KPI strip — open count (with its overdue share), average
                resolution time, lifetime resolved. Pulled from
                /orders/flagged/stats on every refresh. */}
            <div className="dispatch-flagged-kpis">
                <div className="dispatch-flagged-kpi dispatch-flagged-kpi--open">
                    <span className="dispatch-flagged-kpi-icon"><Flag size={16} strokeWidth={2.5} /></span>
                    <span className="dispatch-flagged-kpi-label">{t("flagged_kpi_open")}</span>
                    <span className="dispatch-flagged-kpi-value">
                        {stats?.open_count ?? "—"}
                        {urgentCount > 0 && (
                            <span className="dispatch-flagged-kpi-sub">
                                {t("flagged_kpi_overdue", { count: urgentCount })}
                            </span>
                        )}
                    </span>
                </div>
                <div className="dispatch-flagged-kpi">
                    <span className="dispatch-flagged-kpi-icon"><Timer size={16} strokeWidth={2.5} /></span>
                    <span className="dispatch-flagged-kpi-label">{t("flagged_kpi_avg_resolution")}</span>
                    <span className="dispatch-flagged-kpi-value">{formatAverageSeconds(stats?.avg_resolution_seconds ?? null)}</span>
                </div>
                <div className="dispatch-flagged-kpi dispatch-flagged-kpi--resolved">
                    <span className="dispatch-flagged-kpi-icon"><CheckCircle size={16} strokeWidth={2.5} /></span>
                    <span className="dispatch-flagged-kpi-label">{t("flagged_kpi_resolved")}</span>
                    <span className="dispatch-flagged-kpi-value">{stats?.resolved_count ?? "—"}</span>
                </div>
                {picksTile && (picksTile.available || picksTile.open > 0 || foundModuleOn) && (
                    <button
                        type="button"
                        className={`dispatch-flagged-kpi dispatch-flagged-kpi--picks${picksView ? " dispatch-flagged-kpi--active" : ""}`}
                        onClick={() => setPicksView(v => !v)}
                        aria-pressed={picksView}
                        data-testid="flagged-picks-tile"
                    >
                        <span className="dispatch-flagged-kpi-icon"><PackageSearch size={16} strokeWidth={2.5} /></span>
                        <span className="dispatch-flagged-kpi-label">{picksTile.available ? t("flagged_picks_tile") : t("flagged_found_tile")}</span>
                        <span className="dispatch-flagged-kpi-value">
                            {picksTile.open}
                            <span className="dispatch-flagged-kpi-sub">{picksTile.available ? t("flagged_picks_tile_sub", { count: picksTile.open }) : t("flagged_found_tile_sub")}</span>
                        </span>
                    </button>
                )}
            </div>

            {picksView ? (
                <FlaggedPicksPanel
                    warehouse={(warehouseParam || pickedWarehouse || "").startsWith("country:") ? "" : (warehouseParam || pickedWarehouse || "")}
                    canWrite={!readOnly}
                    onBack={() => setPicksView(false)}
                    onOrdersChanged={() => setRefreshTick(n => n + 1)}
                />
            ) : (<>

            {/* One toolbar card holds search, the date-preset segmented
                control, the custom range and the warehouse / sort pickers —
                same pattern as the Search Orders toolbar. Replaces the old
                stack of a search bar, a pill row and four labelled filter
                groups, which ate a third of the viewport before the first
                flagged order appeared. */}
            <div className="dispatch-search-toolbar">
                <div className="dispatch-search-toolbar-search">
                    <Search size={16} />
                    <input
                        type="search"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder={t("search_placeholder_flagged")}
                    />
                </div>

                <div className="dispatch-search-toolbar-divider" aria-hidden="true" />

                <div className="dispatch-date-presets dispatch-date-presets--segmented">
                    {DATE_PRESETS.map(p => (
                        <button
                            key={p}
                            type="button"
                            className={`dispatch-date-preset ${datePreset === p ? "dispatch-date-preset--active" : ""}`}
                            onClick={() => applyPreset(p)}
                        >
                            {t(`filter_${p}`)}
                        </button>
                    ))}
                </div>

                <div className="dispatch-search-toolbar-divider" aria-hidden="true" />

                <div className="dispatch-search-toolbar-fields">
                    <label className="dispatch-search-field">
                        <span className="dispatch-search-field-label">{t("filter_from")}</span>
                        <input
                            type="date"
                            className="dispatch-search-date"
                            value={dateFrom}
                            onChange={e => { setDateFrom(e.target.value); setDatePreset("custom"); }}
                        />
                    </label>
                    <label className="dispatch-search-field">
                        <span className="dispatch-search-field-label">{t("filter_to")}</span>
                        <input
                            type="date"
                            className="dispatch-search-date"
                            value={dateTo}
                            onChange={e => { setDateTo(e.target.value); setDatePreset("custom"); }}
                        />
                    </label>
                    <label className="dispatch-search-field">
                        <span className="dispatch-search-field-label">{t("filter_warehouse")}</span>
                        <select
                            className="dispatch-search-select"
                            value={warehouse}
                            onChange={e => setWarehouse(e.target.value)}
                            disabled={!warehouseScope.canCross}
                        >
                            {warehouseScope.canCross && <option value="">{t("filter_all_warehouses")}</option>}
                            {(warehousesByCountry.length === 0 || !warehouseScope.canCross) && warehouse && (
                                // /settings not loaded (yet, or at all): keep the
                                // user's own warehouse selectable so the default
                                // scope never silently vanishes from the list.
                                <option value={warehouse}>{warehouse}</option>
                            )}
                            {warehouseScope.canCross && warehousesByCountry.map(g => (
                                <optgroup key={g.country} label={g.country}>
                                    <option value={`country:${g.country}`}>
                                        {t("filter_country_all", { country: g.country })}
                                    </option>
                                    {g.warehouses.map(w => (
                                        <option key={w} value={w}>{w}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </label>
                    <label className="dispatch-search-field">
                        <span className="dispatch-search-field-label">{t("filter_issue")}</span>
                        <select
                            className="dispatch-search-select"
                            value={issueFilter}
                            onChange={e => setIssueFilter(e.target.value as "all" | FlagIssueKind)}
                        >
                            <option value="all">{t("flag_issue_all")}</option>
                            <option value="packing">{t("flag_issue_packing")}</option>
                            <option value="missing">{t("flag_issue_missing")}</option>
                        </select>
                    </label>
                    <label className="dispatch-search-field">
                        <span className="dispatch-search-field-label">{t("flagged_sort")}</span>
                        <select
                            className="dispatch-search-select"
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value as FlaggedSort)}
                        >
                            <option value="oldest">{t("flagged_sort_oldest")}</option>
                            <option value="newest">{t("flagged_sort_newest")}</option>
                        </select>
                    </label>
                    <button
                        type="button"
                        className={`dispatch-search-sort-dir dispatch-search-refresh${loading ? " dispatch-search-refresh--busy" : ""}`}
                        onClick={() => setRefreshTick(n => n + 1)}
                        disabled={loading}
                        title={t("refresh")}
                        aria-label={t("refresh")}
                    >
                        <RefreshCw size={14} />
                    </button>
                </div>
            </div>

            {/* Result count + the scanner affordance. The page has always
                listened for a FLG barcode; nothing on screen said so. */}
            <div className="dispatch-flagged-meta">
                <span>
                    {query.trim()
                        ? t("search_results_count_filtered", { count: filtered.length, total: orders.length })
                        : t("search_results_count", { count: filtered.length })}
                </span>
                <span className="dispatch-flagged-scan-hint">
                    <ScanLine size={14} />
                    {t("flagged_scan_hint")}
                </span>
            </div>

            {/* Only the FIRST load blanks the list. A Refresh click keeps the
                cards on screen — swapping a read worklist for a spinner
                loses the leader's place for the sake of a 300ms fetch. */}
            {loading && orders.length === 0 ? (
                <div className="dispatch-flagged-empty">
                    <FlowLoader message={t("loading")} />
                </div>
            ) : orders.length === 0 ? (
                <div className="dispatch-flagged-empty">
                    <CheckCircle size={48} strokeWidth={1.5} />
                    <p>{t("no_flagged_orders")}</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="dispatch-flagged-empty">
                    <Search size={48} strokeWidth={1.5} />
                    <p>{t("no_order_found")}</p>
                </div>
            ) : (
                <div className="dispatch-flagged-list">
                    {filtered.map(order => {
                        const missingItems = order.items.filter(i => i.missingCount > 0);
                        const damagedItems = order.items.filter(i => i.damagedCount > 0);
                        const wrongItems = order.items.filter(i => i.wrongItemCount > 0);
                        const hasBreakdown = missingItems.length > 0 || damagedItems.length > 0 || wrongItems.length > 0;
                        const flaggedAt = order.latest_flag?.flagged_at ?? order.created_at;
                        const tier = flagAgeTier(order.latest_flag?.flagged_at, now);
                        const value = formatMoney(order.total, order.currency);
                        const kind = flagIssueKind(order);
                        const parcelCount = order.parcels?.length ?? 0;
                        // The chip already names the family; repeat the
                        // recorded reason only when it adds something
                        // (Label Issue, free text).
                        const reason = order.latest_flag?.flagged_reason?.trim() ?? "";
                        const reasonShown = reason !== ""
                            && reason !== AUTO_FLAG_REASON
                            && reason.toLowerCase() !== "packing issue";
                        // One sentence per missing sku: the stage it was
                        // lost at, then who / where / when, and only the
                        // shelf the shortfall happened at (never every
                        // shelf the sku was picked from). See
                        // buildFlagStories.
                        const stories = buildFlagStories(
                            shortfallStages[order.order_id] ?? [],
                            pickTraces[order.order_id] ?? [],
                            t,
                            formatDate,
                        );
                        // Replacement stock per short sku — a little box on
                        // each shortfall chip so the leader sees at a
                        // glance whether one can be sent (Alex, 2026-10-13).
                        const availableBySku = new Map(
                            (shortfallStages[order.order_id] ?? []).map(s => [s.sku, s.available]),
                        );
                        const stockBox = (sku: string) => {
                            const a = availableBySku.get(sku);
                            if (!a) return null;
                            const none = a.total <= 0;
                            const highOnly = !none && a.low <= 0;
                            const text = none
                                ? t("flag_stock_none")
                                : highOnly
                                    ? t("flag_stock_high_only", { n: String(a.total) })
                                    : a.high > 0
                                        ? t("flag_stock_some_high", { n: String(a.total), high: String(a.high) })
                                        : t("flag_stock_available", { n: String(a.total) });
                            // The hover text must agree with the chip: a
                            // sku with nothing left used to still say
                            // "Stock available to send" on hover.
                            const title = none
                                ? t("flag_stock_title_none")
                                : highOnly
                                    ? t("flag_stock_title_high")
                                    : t("flag_stock_title");
                            return (
                                <span
                                    className={`dispatch-flagged-stock dispatch-flagged-stock--${none ? "none" : highOnly ? "high" : "ok"}`}
                                    title={title}
                                >
                                    {none ? <PackageMinus size={12} /> : <PackageSearch size={12} />}
                                    {text}
                                </span>
                            );
                        };
                        return (
                            <div
                                key={order.order_id}
                                className={`dispatch-flagged-card dispatch-flagged-card--${tier}`}
                            >
                                {/* Identity rail — who/what, at a glance */}
                                <div className="dispatch-flagged-card-id">
                                    <p className="dispatch-flagged-order-number">
                                        {t("order_title_label")} #{order.order_number}
                                    </p>
                                    {order.latest_flag?.flagged_id && (
                                        <span className="dispatch-flagged-id">{order.latest_flag.flagged_id}</span>
                                    )}
                                    {order.customer?.name && (
                                        <p className="dispatch-flagged-customer">{order.customer.name}</p>
                                    )}
                                    {/* Warehouse · line count · order value — the
                                        cheap signals a leader weighs before
                                        picking one card over another. */}
                                    <p className="dispatch-flagged-time">
                                        {[
                                            order.warehouse,
                                            t("flagged_lines_count", { count: order.items.length }),
                                            value,
                                        ].filter(Boolean).join(" · ")}
                                    </p>
                                </div>

                                {/* Why it's here. The issue chip is the
                                    load-bearing signal — it also explains
                                    which resolutions apply (Missing items
                                    has parcels, so Manually fulfil exists;
                                    a Packing issue has none yet). */}
                                <div className="dispatch-flagged-card-detail">
                                    <div className="dispatch-flagged-issue-row">
                                        <span className={`dispatch-flagged-kind dispatch-flagged-kind--${kind}`}>
                                            {kind === "missing"
                                                ? <PackageMinus size={13} />
                                                : <PackageOpen size={13} />}
                                            {t(kind === "missing" ? "flag_issue_missing" : "flag_issue_packing")}
                                        </span>
                                        <span className="dispatch-flagged-issue-by">
                                            {order.latest_flag?.flagged_by_name
                                                ? t("flagged_by_name", { name: order.latest_flag.flagged_by_name })
                                                : t("flagged_reason_user")}
                                        </span>
                                        {kind === "missing" && parcelCount > 0 && (
                                            <span className="dispatch-flagged-issue-parcels">
                                                <Package size={12} />
                                                {t("flagged_parcels_sent", { count: parcelCount })}
                                            </span>
                                        )}
                                    </div>
                                    {reasonShown && (
                                        <p className="dispatch-flagged-issue-reason">{reason}</p>
                                    )}

                                    {/* Per-item breakdown. The aggregate summary
                                        that used to sit in the reason block said
                                        exactly what these chips say — one copy is
                                        enough. */}
                                    {hasBreakdown && (
                                        <div className="dispatch-flagged-items">
                                            {missingItems.map(item => (
                                                <div key={`m-${item.item_id}`} className="dispatch-flagged-item dispatch-flagged-item--missing">
                                                    <PackageMinus size={14} />
                                                    <span className="dispatch-flagged-item-sku">{displayItemCode(item.sku, item.barcode)}</span>
                                                    <span className="dispatch-flagged-item-qty">
                                                        x{item.missingCount} {t("item_missing")}
                                                    </span>
                                                    {stockBox(item.sku)}
                                                </div>
                                            ))}
                                            {damagedItems.map(item => (
                                                <div key={`d-${item.item_id}`} className="dispatch-flagged-item dispatch-flagged-item--damaged">
                                                    <Package size={14} />
                                                    <span className="dispatch-flagged-item-sku">{displayItemCode(item.sku, item.barcode)}</span>
                                                    <span className="dispatch-flagged-item-qty">
                                                        x{item.damagedCount} {t("item_damaged")}
                                                    </span>
                                                    {stockBox(item.sku)}
                                                </div>
                                            ))}
                                            {wrongItems.map(item => (
                                                <div key={`w-${item.item_id}`} className="dispatch-flagged-item dispatch-flagged-item--damaged">
                                                    <RefreshCw size={14} />
                                                    <span className="dispatch-flagged-item-sku">{displayItemCode(item.sku, item.barcode)}</span>
                                                    <span className="dispatch-flagged-item-qty">
                                                        x{item.wrongItemCount} {t("item_wrong")}
                                                    </span>
                                                    {stockBox(item.sku)}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* What went wrong, one sentence per missing
                                        sku: "1 missing at picking — Lel checked
                                        H20L3 at 12:49 and found none". */}
                                    {stories.length > 0 && (
                                        <ul className="dispatch-flagged-trace" aria-label={t("flag_story_title")}>
                                            {stories.map(st => (
                                                <li key={`story-${st.sku}`} className="dispatch-flagged-trace-row dispatch-flagged-trace-row--short">
                                                    <Route size={13} />
                                                    <span className="dispatch-flagged-trace-sku">{st.sku}</span>
                                                    <span className="dispatch-flagged-story">
                                                        {st.segments.map((seg, i) => (
                                                            <span key={i} className="dispatch-flagged-story-seg">
                                                                <strong>{seg.heading}</strong>
                                                                {seg.text ? ` — ${seg.text}` : ""}
                                                                {seg.chips.map(c => (
                                                                    <span
                                                                        key={c.label}
                                                                        className="dispatch-flagged-trace-loc"
                                                                        title={c.high ? t("flag_story_high_shelf") : undefined}
                                                                    >
                                                                        {c.label}{c.qty !== null ? ` ×${c.qty}` : ""}
                                                                    </span>
                                                                ))}
                                                                {seg.more > 0 ? ` ${t("flag_story_more_shelves", { n: String(seg.more) })}` : ""}
                                                            </span>
                                                        ))}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {/* How long it's waited, and the one action */}
                                <div className="dispatch-flagged-card-action">
                                    <span
                                        className={`dispatch-flagged-duration dispatch-flagged-duration--${tier}`}
                                        title={formatDate(flaggedAt)}
                                    >
                                        {formatFlagDuration(order.latest_flag?.flagged_at, now)}
                                    </span>
                                    <span className="dispatch-flagged-time">{formatDate(flaggedAt)}</span>
                                    <FlaggedResolveSplitButton
                                        order={order}
                                        onDispatch={() => navigate(`/dispatch/flagged/${order.order_id}`)}
                                        onRefund={() => setQuickAction({ order, mode: "refund" })}
                                        onManualShip={() => setQuickAction({ order, mode: "ship" })}
                                        onReplace={() => openReplace(order)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            </>)}

            {quickAction?.mode === "refund" && (
                <FlaggedRefundModal
                    order={quickAction.order}
                    busy={quickBusy}
                    onCancel={() => setQuickAction(null)}
                    onConfirm={handleQuickRefund(quickAction.order)}
                />
            )}
            {quickAction?.mode === "ship" && (
                <FlaggedManualShipModal
                    order={quickAction.order}
                    busy={quickBusy}
                    onCancel={() => setQuickAction(null)}
                    onConfirm={handleQuickShip(quickAction.order)}
                />
            )}
            {quickAction?.mode === "replace" && (
                <ReplacementOrderModal
                    order={quickAction.order}
                    flagged={{ outstanding: quickAction.outstanding }}
                    onClose={() => setQuickAction(null)}
                    onCreated={handleReplacementCreated(quickAction.order)}
                    onConflict={() => { setQuickAction(null); setRefreshTick(n => n + 1); }}
                />
            )}

            {/* Global scanner — scan a flagged-ID barcode to jump to the order */}
            <HiddenScanInput value={scan} onChange={setScan} onSubmit={handleScan} disabled={false} />
        </div>
    );
};

export default DispatchFlagged;
